import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../services/db';
import { useAuth } from '../context/AuthContext';
import { createEnterpriseUser } from '../services/authService';
import { User } from '../types';
import { PlusCircle, Users, Award, Ticket, CreditCard, Building, ArrowLeft, Shield, AlertCircle } from 'lucide-react';

const AdminDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [recentUsers, setRecentUsers] = useState<User[]>([]);
  const [recentTests, setRecentTests] = useState<any[]>([]);
  const [vouchers, setVouchers] = useState<any[]>([]);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalTests: 0,
    passRate: 0,
    activeVouchers: 0,
    successfulPayments: 0,
    failedPayments: 0,
    failedLogins24h: 0
  });
  const [showVoucherModal, setShowVoucherModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    company: ''
  });
  const [inviteSentMessage, setInviteSentMessage] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<{
    name?: string;
    email?: string;
    company?: string;
    submit?: string;
  }>({});
  const [isSubmittingUser, setIsSubmittingUser] = useState(false);

  useEffect(() => {
    const checkAdmin = async () => {
      if (!isAuthenticated || !user) {
        navigate('/login');
        return;
      }

      // Check if user has admin privileges
      if (!user.isAdmin && !user.isSuperAdmin) {
        navigate('/');
        return;
      }
      
      setIsAdmin(true);
      loadDashboardData();
    };

    checkAdmin();
  }, [navigate, isAuthenticated, user]);

  const loadDashboardData = async () => {
    try {
      // For development, use mock data
      if (import.meta.env.DEV) {
        setRecentUsers([
          {
            id: '1',
            name: 'Test User 1',
            company: 'Test Company 1',
            created_at: new Date().toISOString(),
            email: 'test1@example.com',
            billingAddress: 'Test Address 1',
            courseProgress: { modulesCompleted: {}, allCompleted: false },
            completedModules: []
          },
          {
            id: '2',
            name: 'Test User 2',
            company: 'Test Company 2',
            created_at: new Date().toISOString(),
            email: 'test2@example.com',
            billingAddress: 'Test Address 2',
            courseProgress: { modulesCompleted: {}, allCompleted: false },
            completedModules: []
          }
        ]);

        setRecentTests([
          {
            id: '1',
            users: { name: 'Test User 1', company: 'Test Company 1' },
            score: 85,
            passed: true,
            created_at: new Date().toISOString()
          },
          {
            id: '2',
            users: { name: 'Test User 2', company: 'Test Company 2' },
            score: 75,
            passed: true,
            created_at: new Date().toISOString()
          }
        ]);

        setVouchers([
          {
            code: 'START50',
            partner: 'Digital Academy',
            value: 50,
            discountType: 'percentage',
            usesLeft: 45
          },
          {
            code: 'WELCOME25',
            partner: 'Tech Conference',
            value: 25,
            discountType: 'percentage',
            usesLeft: 30
          }
        ]);

        setTransactions([
          {
            id: '1',
            user: { name: 'Test User 1', company: 'Company A' },
            amount: 49.90,
            status: 'succeeded',
            created_at: new Date().toISOString()
          },
          {
            id: '2',
            user: { name: 'Test User 2', company: 'Company B' },
            amount: 49.90,
            status: 'failed',
            created_at: new Date().toISOString()
          }
        ]);

        setStats({
          totalUsers: 10,
          totalTests: 15,
          passRate: 80,
          activeVouchers: 5,
          successfulPayments: 25,
          failedPayments: 2
        });

        setLoading(false);
        return;
      }

      // Call secure Edge Function for admin dashboard data
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        throw new Error('Authentication session not found');
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-admin-dashboard-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch dashboard data');
      }

      const result = await response.json();
      const { recentUsers, recentTests, vouchers, transactions, stats } = result.data;

      setRecentUsers(recentUsers);
      setRecentTests(recentTests);
      setVouchers(vouchers);
      setTransactions(transactions);

      // Fetch login attempt stats if user is super admin
      let failedLogins24h = 0;
      if (user?.isSuperAdmin) {
        try {
          const loginAttemptsResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-login-attempts`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
              timeRange: '24h',
              limit: 1,
            }),
          });

          if (loginAttemptsResponse.ok) {
            const loginResult = await loginAttemptsResponse.json();
            failedLogins24h = loginResult.data?.stats?.failed24h || 0;
          }
        } catch (err) {
          console.error('Error fetching login stats:', err);
        }
      }

      setStats({ ...stats, failedLogins24h });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateUserForm = () => {
    const errors: typeof formErrors = {};
    
    if (!userForm.name.trim()) {
      errors.name = 'Name ist erforderlich';
    }
    
    if (!userForm.email.trim()) {
      errors.email = 'E-Mail ist erforderlich';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(userForm.email.trim())) {
        errors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
      }
    }
    
    if (!userForm.company.trim()) {
      errors.company = 'Unternehmen ist erforderlich';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEnterpriseUserSubmit = async () => {
    // Reset errors
    setFormErrors({});
    
    // Validate form
    if (!validateUserForm()) {
      return;
    }
    
    setIsSubmittingUser(true);
    
    try {
      const { error } = await createEnterpriseUser({
        name: userForm.name.trim(),
        email: userForm.email.trim(),
        company: userForm.company.trim()
      });

      if (error) {
        setFormErrors({ submit: error });
        return;
      }

      setInviteSentMessage(`Einladung wurde an ${userForm.email.trim()} gesendet.`);
      await loadDashboardData();

      setShowUserModal(false);

      setUserForm({
        name: '',
        email: '',
        company: ''
      });
    } catch (error) {
      console.error('Error creating enterprise user:', error);
      setFormErrors({ 
        submit: error instanceof Error ? error.message : 'Fehler beim Anlegen des Benutzers' 
      });
    } finally {
      setIsSubmittingUser(false);
    }
  };

  if (!isAdmin || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16 pb-12">
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Link 
              to="/"
              className="text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft className="h-6 w-6" />
            </Link>
            <h1 className="text-3xl font-bold text-gray-800">Admin Dashboard</h1>
          </div>
          <p className="text-gray-600 mt-2">
            Klicken Sie auf die Karten für detaillierte Übersichten
          </p>
          {import.meta.env.DEV && (
            <p className="text-sm text-yellow-600 mt-2">
              Development Mode: Using mock data
            </p>
          )}
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div 
            onClick={() => navigate('/admin/users')}
            className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center">
              <Users className="h-8 w-8 text-blue-500" />
              <div className="ml-4">
                <p className="text-sm text-gray-600">Gesamtnutzer</p>
                <p className="text-2xl font-bold text-gray-800">{stats.totalUsers}</p>
              </div>
            </div>
          </div>

          <div 
            onClick={() => navigate('/admin/tests')}
            className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center">
              <Award className="h-8 w-8 text-green-500" />
              <div className="ml-4">
                <p className="text-sm text-gray-600">Bestandene Tests</p>
                <p className="text-2xl font-bold text-gray-800">{Math.round(stats.passRate)}%</p>
              </div>
            </div>
          </div>

          <div 
            onClick={() => navigate('/admin/vouchers')}
            className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center">
              <Ticket className="h-8 w-8 text-purple-500" />
              <div className="ml-4">
                <p className="text-sm text-gray-600">Aktive Voucher</p>
                <p className="text-2xl font-bold text-gray-800">{stats.activeVouchers}</p>
              </div>
            </div>
          </div>

          <div 
            onClick={() => navigate('/admin/transactions')}
            className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center">
              <CreditCard className="h-8 w-8 text-emerald-500" />
              <div className="ml-4">
                <p className="text-sm text-gray-600">Erfolgreiche Zahlungen</p>
                <p className="text-2xl font-bold text-gray-800">{stats.successfulPayments}</p>
              </div>
            </div>
          </div>

          <div
            onClick={() => navigate('/admin/transactions')}
            className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center">
              <CreditCard className="h-8 w-8 text-red-500" />
              <div className="ml-4">
                <p className="text-sm text-gray-600">Fehlgeschlagene Zahlungen</p>
                <p className="text-2xl font-bold text-gray-800">{stats.failedPayments}</p>
              </div>
            </div>
          </div>

          {user?.isSuperAdmin && (
            <div
              onClick={() => navigate('/admin/login-attempts')}
              className={`bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow ${
                stats.failedLogins24h > 10 ? 'border-2 border-red-400' : ''
              }`}
            >
              <div className="flex items-center">
                <Shield className={`h-8 w-8 ${stats.failedLogins24h > 10 ? 'text-red-500' : 'text-orange-500'}`} />
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Fehlgeschlagene Logins (24h)</p>
                  <p className={`text-2xl font-bold ${stats.failedLogins24h > 10 ? 'text-red-600' : 'text-gray-800'}`}>
                    {stats.failedLogins24h}
                  </p>
                </div>
                {stats.failedLogins24h > 10 && (
                  <AlertCircle className="h-5 w-5 text-red-500 ml-auto" />
                )}
              </div>
            </div>
          )}

          <div
            onClick={() => setShowUserModal(true)}
            className="bg-white rounded-lg shadow p-6 cursor-pointer hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center">
              <Building className="h-8 w-8 text-blue-500" />
              <div className="ml-4">
                <p className="text-sm text-gray-600">Enterprise Kunden</p>
                <button
                  onClick={() => setShowUserModal(true)}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  Nutzer anlegen
                </button>
              </div>
            </div>
          </div>
        </div>

        {inviteSentMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4" role="alert">
            <span className="block sm:inline">{inviteSentMessage}</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Recent Users Preview */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800">Neue Nutzer</h2>
            </div>
            <div className="p-6">
              <div className="divide-y divide-gray-200">
                {recentUsers.map(user => (
                  <div key={user.id} className="py-4">
                    <p className="font-medium text-gray-800">{user.name}</p>
                    <p className="text-sm text-gray-600">{user.company}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(user.created_at).toLocaleDateString('de-DE')}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recent Tests Preview */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800">Letzte Prüfungen</h2>
            </div>
            <div className="p-6">
              <div className="divide-y divide-gray-200">
                {recentTests.map(test => (
                  <div key={test.id} className="py-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-800">
                          {test.users.name}
                        </p>
                        <p className="text-sm text-gray-600">
                          {test.users.company}
                        </p>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-sm ${
                        test.passed 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {test.score}%
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(test.created_at).toLocaleDateString('de-DE')}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Active Vouchers Preview */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800">Aktive Voucher</h2>
            </div>
            <div className="p-6">
              <div className="divide-y divide-gray-200">
                {vouchers.slice(0, 5).map(voucher => (
                  <div key={voucher.code} className="py-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-800">{voucher.code}</p>
                        <p className="text-sm text-gray-600">{voucher.partner}</p>
                      </div>
                      <div className="text-sm">
                        <span className="font-medium">
                          {voucher.discount_value}{voucher.discount_type === 'percentage' ? '%' : '€'}
                        </span>
                        <span className="text-gray-500 ml-2">
                          ({voucher.remaining_uses} übrig)
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recent Payments Preview */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800">Letzte Zahlungen</h2>
            </div>
            <div className="p-6">
              <div className="divide-y divide-gray-200">
                {transactions.slice(0, 5).map(transaction => (
                  <div key={transaction.id} className="py-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-800">
                          {transaction.user.name}
                        </p>
                        <p className="text-sm text-gray-600">
                          {transaction.amount.toFixed(2)} €
                        </p>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-sm ${
                        transaction.status === 'succeeded'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {transaction.status === 'succeeded' ? 'Erfolgreich' : 'Fehlgeschlagen'}
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(transaction.created_at).toLocaleDateString('de-DE')}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Enterprise User Modal */}
        {showUserModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6 relative">
              <button
                onClick={() => setShowUserModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <h3 className="text-xl font-semibold mb-4">
                Enterprise Nutzer anlegen
              </h3>
              
              {formErrors.submit && (
                <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-md border border-red-200">
                  {formErrors.submit}
                </div>
              )}
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    value={userForm.name}
                    onChange={(e) => setUserForm(prev => ({ ...prev, name: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      formErrors.name ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="Max Mustermann"
                    disabled={isSubmittingUser}
                  />
                  {formErrors.name && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    E-Mail
                  </label>
                  <input
                    type="email"
                    value={userForm.email}
                    onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      formErrors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="max.mustermann@example.com"
                    disabled={isSubmittingUser}
                  />
                  {formErrors.email && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unternehmen
                  </label>
                  <input
                    type="text"
                    value={userForm.company}
                    onChange={(e) => setUserForm(prev => ({ ...prev, company: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      formErrors.company ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="Musterfirma GmbH"
                    disabled={isSubmittingUser}
                  />
                  {formErrors.company && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.company}</p>
                  )}
                </div>
              </div>
              <div className="flex justify-end mt-6">
                <button
                  type="button"
                  onClick={() => setShowUserModal(false)}
                  className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 mr-2 disabled:opacity-50"
                  disabled={isSubmittingUser}
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleEnterpriseUserSubmit}
                  disabled={isSubmittingUser}
                  className={`px-4 py-2 rounded-md transition-colors ${
                    isSubmittingUser
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {isSubmittingUser ? 'Wird gesendet...' : 'Speichern'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboardPage;